import React from 'react'

import cx from 'classnames'
import { Field } from 'formik'

import { Div, H5, P, ToolTip } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import useClassifications from '../../../hooks/useClassifications'
import useFilterSets from '../../../hooks/useFilterSets'
import { ReactComponent as Email } from '../../../svgs/Email.svg'
import { ReactComponent as SiteVisit } from '../../../svgs/SiteVisit.svg'
import C from '../constants'
import CheckboxRow from '../form/CheckboxRow'
import LoadingFieldList from '../form/LoadingFieldList'
import LoadingFieldRow from '../form/LoadingFieldRow'
import MultiSelectCheckboxRow from '../form/MultiSelectCheckboxRow'

import styles from '../styles.module.css'

interface AccountNotificationProps {
  hasSavedViewEmailDigest: boolean
  hasSavedViewNotifications: boolean
  onSelectSavedFilter: (filter: any) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

const SiteVisitIcon: React.FC<any> = ({ active }) => (
  <SiteVisit className={cx(styles.iconStroke, { [styles.active]: active })} />
)

const EmailIcon: React.FC<any> = ({ active }) => (
  <Email className={cx(styles.iconFill, { [styles.active]: active })} />
)

const IconButton: React.FC<any> = ({ onClick, icon, tooltip, active }) => {
  const onIconClick = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    onClick()
  }
  return (
    <ToolTip flow="left" component={tooltip}>
      <Div
        onClick={onIconClick}
        className={cx(styles.iconButton, { [styles.activeIconButton]: active })}>
        {icon}
      </Div>
    </ToolTip>
  )
}

const SavedFilterRow: React.FC<any> = ({
  filter,
  onSelectSavedFilter,
  onSavedFilterChange,
  hasSavedViewEmailDigest,
  hasSavedViewNotifications
}) => {
  return (
    <Div
      onClick={() => onSelectSavedFilter(filter)}
      key={filter.id}
      className={styles.savedFilterWrapper}>
      <Div key={filter.id} className={styles.row}>
        <Div className={styles.alignCenter}>{filter.name}</Div>
        <Div className={styles.alignCenter}>
          <Div className={styles.alignCenter}>
            {hasSavedViewNotifications && (
              <Field name={`savedFilters.${C.SAVED_FILTER_TYPE.PUSH}-${filter.id}`}>
                {(props: any) => (
                  <IconButton
                    icon={<SiteVisitIcon active={props.field.value} />}
                    tooltip={`Push notifications ${props.field.value ? 'on' : 'off'}`}
                    active={props.field.value}
                    onClick={() => onSavedFilterChange(props.form, props.field)}
                  />
                )}
              </Field>
            )}
            {hasSavedViewEmailDigest && (
              <Field name={`savedFilters.${C.SAVED_FILTER_TYPE.EMAIL}-${filter.id}`}>
                {(props: any) => (
                  <IconButton
                    icon={<EmailIcon active={props.field.value} />}
                    tooltip={`Daily digest emails ${props.field.value ? 'on' : 'off'}`}
                    active={props.field.value}
                    onClick={() => onSavedFilterChange(props.form, props.field)}
                  />
                )}
              </Field>
            )}
          </Div>

          <Div className={styles.arrowButton}>
            <Icon name="arrow-1-right" width={9} />
          </Div>
        </Div>
      </Div>
    </Div>
  )
}

const AccountNotificationSection: React.FC<AccountNotificationProps> = ({
  hasSavedViewEmailDigest,
  hasSavedViewNotifications,
  onSelectSavedFilter,
  setFieldValue
}) => {
  const {
    loadingClassifications,
    classifications,
    classificationsFailed,
    fetchClassifications
  } = useClassifications()

  const {
    savedFilters,
    loadingAccountActivity,
    savedFiltersFailed,
    fetchFilterSets
  } = useFilterSets(undefined, setFieldValue)

  const onOutreachChange = (form: any, field: any) => {
    const { values, setFieldTouched, setValues } = form
    const currentSettings = values.properties.outboundNotificationSettings
    const setting = {
      outboundClickNotification: !field.value,
      outboundOpenNotification: currentSettings?.outboundOpenNotification || false,
      outboundPageViewNotification: currentSettings?.outboundPageViewNotification || false
    }
    setValues({
      ...values,
      properties: {
        ...values.properties,
        has_outreach_notifications: !field.value,
        outboundNotificationSettings: setting
      }
    })
    setFieldTouched('properties.outboundNotificationSettings')
  }

  const onSequenceChange = (form: any, field: any) => {
    const { values, setFieldValue, setFieldTouched } = form
    const currentSettings = values.properties.outboundNotificationSettings
    const setting = {
      outboundClickNotification: currentSettings?.outboundClickNotification || false,
      outboundOpenNotification: !currentSettings?.outboundOpenNotification,
      outboundPageViewNotification: !currentSettings?.outboundPageViewNotification
    }
    setFieldValue(field.name, setting)
    setFieldTouched(field.name)
  }

  const onSavedFilterChange = (form: any, field: any) => {
    const { setFieldValue } = form
    setFieldValue(field.name, !field.value)
  }

  return (
    <section>
      <H5>Account Activity</H5>
      {!hasSavedViewNotifications && (
        <Field name="properties.follow_accounts">
          {(props: any) => <CheckboxRow {...props} content="Target account activity on my site" />}
        </Field>
      )}
      <LoadingFieldList
        loading={loadingAccountActivity}
        failed={savedFiltersFailed}
        label="saved filters"
        handleRetry={fetchFilterSets}>
        <>
          {savedFilters
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((filter: any) => (
              <SavedFilterRow
                key={filter.id}
                filter={filter}
                onSelectSavedFilter={onSelectSavedFilter}
                onSavedFilterChange={onSavedFilterChange}
                hasSavedViewEmailDigest={hasSavedViewEmailDigest}
                hasSavedViewNotifications={hasSavedViewNotifications}
              />
            ))}
        </>
      </LoadingFieldList>
      <Field name="properties.has_outreach_notifications">
        {(props: any) => (
          <CheckboxRow
            {...props}
            onChange={() => onOutreachChange(props.form, props.field)}
            content={
              <Div>
                Contact activity on my site from an Outreach Sequence Email
                <P className={styles.subtext}>
                  Notifications will be sent to the Sequence creator. The Outreach integration must
                  be connected.
                </P>
              </Div>
            }
          />
        )}
      </Field>
      <Field name="properties.outboundNotificationSettings">
        {(props: any) => (
          <CheckboxRow
            {...props}
            content="Contact opens my Drift Sequence or tracked email, and when they open a link in it"
            onChange={() => onSequenceChange(props.form, props.field)}
            checkedCondition={
              props.field.value?.outboundOpenNotification &&
              props.field.value?.outboundPageViewNotification
            }
          />
        )}
      </Field>
      <Field name="properties.classification-notifications">
        {({ field: checkBoxField, form, ...rest }: any) => (
          <LoadingFieldRow
            loading={loadingClassifications}
            failed={classificationsFailed}
            handleRetry={fetchClassifications}
            label="lead stage">
            <MultiSelectCheckboxRow
              {...rest}
              label="Contact changes their lead stage"
              selectFieldName="properties.classifications-to-notify-on"
              form={form}
              checkBoxField={checkBoxField}
              options={classifications}
            />
          </LoadingFieldRow>
        )}
      </Field>
    </section>
  )
}

export default AccountNotificationSection
